/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@Button';
import Text from '@Text';
import FetchStatus from '../../Data/FetchStatus';
import { getClientApiSlotsAction } from '../../Hooks/actionCreators';
import { selectUpstreamLocationStepKey } from '../../Helpers/clientApiJourneyPosition.helpers';
import {
  goToNextScreen,
  goToPreviousScreen,
  setSelectedSlot,
  setStepState,
} from '../../Lib/redux/slices/clientApiJourney';
import { useWidgetHeightRefresh } from '../../Hooks/useWidgetHeightRefresh/useWidgetHeightRefresh';
import {
  groupSlotsByDay,
  getSlotDateHeadingLabel,
  getSlotTimeRangeLabel,
} from './slotDateTime.helpers';
import {
  getById,
  getByTestId,
  outerHeight,
  useOlbScrollMaxHeight,
  journeyProgressHeight,
} from './clientApiLayout.helpers';
import { useFallThroughToNormalLead } from '../../Hooks/useFallThroughToNormalLead';
import { FALL_THROUGH_REASONS } from './clientApiJourney.constants';
import AppointmentAccordion, {
  EXPAND_TRANSITION_MS,
} from './AppointmentAccordion';
import FailureNotice from './FailureNotice';
import makeSolarSlotsStyles from './makeSolarSlotsStyles';
import useClientApiCopy from './copy/useClientApiCopy';

const SPINNER_DELAY_MS = 200;

const SolarSlots = ({ stepKey }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = makeSolarSlotsStyles({ theme });
  const copy = useClientApiCopy();
  const scrollRef = useRef(null);
  // Bumped when the accordion finishes animating so the scroll cap re-measures
  // against the new height (fixed-height mode only observes formContentContainer).
  const [accordionSettled, setAccordionSettled] = useState(0);
  const [accordionToggleCount, setAccordionToggleCount] = useState(0);

  const localeCode = useSelector((store) => store.formConfig?.localeCode);
  const selectedLocation = useSelector(
    (store) => store.clientApiJourney.location?.selected
  );
  const selectedSlot = useSelector(
    (store) => store.clientApiJourney.appointment?.selectedSlot
  );
  const isDynamicHeightEnabled = useSelector(
    (store) => store.optionalFormConfig?.isDynamicHeightEnabled
  );
  const locationStepKey = useSelector(selectUpstreamLocationStepKey);
  const slotState = useSelector(
    (store) =>
      store.clientApiJourney.location?.slotsByLocation?.[selectedLocation?.id]
  );

  const slotsStatus = slotState?.slotsStatus;

  // The customer has one home, so there is a single slot list. Fetch it once on
  // mount; the guard stops a refetch when returning from a later screen.
  useEffect(() => {
    const isUnfetched =
      slotsStatus === undefined || slotsStatus === FetchStatus.NOT_STARTED;
    if (isUnfetched) {
      dispatch(
        getClientApiSlotsAction({ location: selectedLocation, locationStepKey })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const availableSlots = (slotState?.slots ?? []).filter(
    (slot) => slot.available !== false && slot.start && slot.end
  );
  const days = groupSlotsByDay(availableSlots);

  // An empty result or a failed lookup ends the journey as a normal lead, after
  // a failure notice; a still-loading list must not.
  const hasFailed = slotsStatus === FetchStatus.FAILED;
  const hasNoSlots = slotsStatus === FetchStatus.COMPLETED && days.length === 0;
  const shouldFallThrough = hasFailed || hasNoSlots;

  useWidgetHeightRefresh([slotsStatus, selectedSlot]);

  useFallThroughToNormalLead({
    shouldFallThrough,
    reason: hasFailed
      ? FALL_THROUGH_REASONS.SLOTS_LOOKUP_FAILED
      : FALL_THROUGH_REASONS.NO_SLOTS,
  });

  // Show the loader only if the fetch is still pending after a short delay, so a
  // fast or cached response does not flash a spinner.
  const [showSpinner, setShowSpinner] = useState(false);
  const isLoading =
    slotsStatus === undefined ||
    slotsStatus === FetchStatus.NOT_STARTED ||
    slotsStatus === FetchStatus.IN_PROGRESS;
  useEffect(() => {
    if (!isLoading) {
      setShowSpinner(false);
      return undefined;
    }
    const timer = setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isLoading]);

  const handleBack = () => {
    dispatch(goToPreviousScreen());
  };

  const handleAccordionToggle = () => {
    setAccordionToggleCount((n) => n + 1);
  };

  // Re-measure once the expand/collapse animation has settled. Keyed on the
  // toggle count so the timer is cleared if the screen unmounts or the user
  // toggles again before it fires.
  useEffect(() => {
    if (accordionToggleCount === 0) {
      return undefined;
    }
    const timer = setTimeout(
      () => setAccordionSettled((n) => n + 1),
      EXPAND_TRANSITION_MS + 50
    );
    return () => clearTimeout(timer);
  }, [accordionToggleCount]);

  const handleSelectSlot = (slot) => {
    const isSelected = selectedSlot?.start === slot.start;
    dispatch(
      setSelectedSlot({
        selectedSlot: isSelected
          ? null
          : {
              locationId: selectedLocation.id,
              start: slot.start,
              end: slot.end,
            },
      })
    );
  };

  const handleContinue = () => {
    dispatch(
      setStepState({
        stepKey,
        state: { start: selectedSlot.start, end: selectedSlot.end },
      })
    );
    dispatch(goToNextScreen());
  };

  useOlbScrollMaxHeight(
    scrollRef,
    () => {
      const formContent = getById('formContentContainer');
      if (!formContent) return 0;
      return (
        formContent.clientHeight -
        (formContent.children?.[0]?.clientHeight ?? 0) -
        journeyProgressHeight() -
        outerHeight(getByTestId('solarSlotsBackRow')) -
        outerHeight(getByTestId('solarSlotsHeadingRow')) -
        outerHeight(getByTestId('solarSlotsAccordionRow')) -
        outerHeight(getByTestId('solarSlotsContinueRow')) -
        (getById('footerContainer')?.clientHeight ?? 0)
      );
    },
    [days.length, selectedSlot, isDynamicHeightEnabled, accordionSettled],
    !isDynamicHeightEnabled
  );

  const isSelected = (slot) => selectedSlot?.start === slot.start;

  // Until the slots are known, show only the (delayed) loader: the page would
  // otherwise flash before an empty or failed result swaps it for the failure
  // notice.
  if (isLoading) {
    return (
      <Container css={classes.container} data-testid="solarSlotsLoading">
        {showSpinner && (
          <Box css={classes.spinner} data-testid="solarSlotsSpinner">
            <CircularProgress size={20} />
          </Box>
        )}
      </Container>
    );
  }

  if (shouldFallThrough) {
    return (
      <FailureNotice
        heading={copy.solarSlots.loadFailed.heading}
        subheading={copy.solarSlots.loadFailed.subheading}
        dataTestId="solarSlotsUnavailable"
      />
    );
  }

  return (
    <Container css={classes.container} data-testid="solarSlots">
      <Box css={classes.backButtonContainer} data-testid="solarSlotsBackRow">
        <MuiButton
          css={classes.backLink}
          onClick={handleBack}
          data-testid="solarSlotsBack"
        >
          {copy.solarSlots.back}
        </MuiButton>
      </Box>
      <Box css={classes.heading} data-testid="solarSlotsHeadingRow">
        <Text variant="question">{copy.solarSlots.title}</Text>
        <Box css={classes.subheading}>
          <Text variant="text">{copy.solarSlots.subheading}</Text>
        </Box>
      </Box>

      <Box data-testid="solarSlotsAccordionRow">
        <AppointmentAccordion
          label={copy.solarSlots.accordionLabel}
          body={copy.solarSlots.accordionBody}
          onToggle={handleAccordionToggle}
        />
      </Box>

      <Box
        css={classes.scrollArea}
        ref={scrollRef}
        data-testid="solarSlotsScrollRegion"
      >
        {days.map((day) => (
          <Box
            css={classes.dayGroup}
            key={day.dayKey}
            data-testid={`solarSlotsDay-${day.dayKey}`}
          >
            <Box css={classes.dayHeader}>
              <Text variant="text">
                {getSlotDateHeadingLabel(day.slots[0].start, localeCode)}
              </Text>
            </Box>
            <Box css={classes.pillRow}>
              {day.slots.map((slot) => (
                <MuiButton
                  key={slot.start}
                  css={[classes.pill, isSelected(slot) && classes.pillSelected]}
                  onClick={() => handleSelectSlot(slot)}
                  data-testid={`solarSlotOption-${slot.start}`}
                >
                  {getSlotTimeRangeLabel(slot.start, slot.end)}
                  {isSelected(slot) && <span css={classes.tick}>✓</span>}
                </MuiButton>
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      <Box css={classes.continueContainer} data-testid="solarSlotsContinueRow">
        <Button
          label={copy.solarSlots.continue}
          onClick={handleContinue}
          disabled={!selectedSlot}
          dataTestId="solarSlotsContinue"
        />
      </Box>
    </Container>
  );
};

export default SolarSlots;
